import { useCallback, useEffect, useRef } from 'react';
import { useRenderContext } from '../context/RenderContext';
import { startRender, subscribeToProgress, downloadOutput } from '../services/renderService';
import type { CompositionProps, OutputSettings, RenderJob } from '../types';

/**
 * Primary hook for triggering and tracking a render job.
 * Coordinates RenderContext, renderService, and SSE subscription lifecycle.
 */
export function useRender() {
  const {
    state,
    startRender: ctxStart,
    updateProgress,
    appendLog,
    completeRender,
    failRender,
    resetRender,
  } = useRenderContext();

  // Keep a ref to the SSE cleanup fn so we can close it on unmount or reset
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  const trigger = useCallback(
    async (compositionProps: CompositionProps, outputSettings: OutputSettings) => {
      if (state.isRendering) return;

      // Build a local job shell for context
      const tempJob: RenderJob = {
        id: 'pending',
        templateId: compositionProps.templateId,
        compositionProps,
        outputSettings,
        progress: { jobId: 'pending', stage: 'preparing', percent: 0, message: 'Starting…' },
        createdAt: new Date().toISOString(),
      };
      ctxStart(tempJob);
      appendLog('[INFO] Submitting render job…');

      let jobId: string;
      try {
        const result = await startRender(compositionProps, outputSettings);
        jobId = result.jobId;
        appendLog(`[INFO] Job accepted: ${jobId}`);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to start render';
        failRender(msg);
        appendLog(`[ERROR] ${msg}`);
        return;
      }

      // Subscribe to SSE progress stream
      cleanupRef.current = subscribeToProgress(
        jobId,
        (progress) => {
          updateProgress(progress);
          appendLog(`[${progress.stage.toUpperCase()}] ${progress.message} (${progress.percent}%)`);
        },
        (outputUrl) => {
          completeRender(outputUrl);
          appendLog('[INFO] ✓ Render complete — ready to download');
          cleanupRef.current = null;
        },
        (error) => {
          failRender(error);
          appendLog(`[ERROR] ${error}`);
          cleanupRef.current = null;
        }
      );
    },
    [state.isRendering, ctxStart, updateProgress, appendLog, completeRender, failRender]
  );

  const download = useCallback(() => {
    const jobId = state.progress?.jobId;
    if (!jobId || state.progress?.stage !== 'completed') return;
    downloadOutput(jobId, `news-render-${jobId.slice(0, 8)}.mp4`);
  }, [state.progress]);

  const reset = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    resetRender();
  }, [resetRender]);

  return {
    isRendering: state.isRendering,
    progress: state.progress,
    logs: state.logs,
    isComplete: state.progress?.stage === 'completed',
    hasError: state.progress?.stage === 'error',
    trigger,
    download,
    reset,
  };
}
